import { clients } from './clients';
import { accountsReceivable } from './accountsReceivable';
import { invoices } from './invoices';
import { billings } from './billing';
import { documents } from './documents';
import { suppliers } from './suppliers';
import { accountsPayable } from './accountsPayable';
import { products } from './products';
import { contracts } from './contracts';
import { adjustments } from './adjustments';
import { indices } from './indices';
import { transactions } from './transactions';
import { bankAccounts } from './bankAccounts';
import { passwords } from './passwords';

export function searchAll(query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];

  const groups = [];

  const matchedClients = clients.filter(
    (item) =>
      item.name.toLowerCase().includes(q) ||
      item.fantasyName.toLowerCase().includes(q) ||
      item.document.includes(q),
  );
  if (matchedClients.length) {
    groups.push({
      type: 'Clientes',
      items: matchedClients.map((item) => ({
        id: item.id,
        label: item.name,
        href: `/cadastros/clientes/${item.id}`,
      })),
    });
  }

  const matchedSuppliers = suppliers.filter(
    (item) =>
      item.legalName.toLowerCase().includes(q) ||
      item.fantasyName.toLowerCase().includes(q) ||
      item.document.includes(q),
  );
  if (matchedSuppliers.length) {
    groups.push({
      type: 'Fornecedores',
      items: matchedSuppliers.map((item) => ({
        id: item.id,
        label: item.fantasyName,
        href: `/cadastros/fornecedores/${item.id}`,
      })),
    });
  }

  const matchedReceivables = accountsReceivable.filter(
    (item) =>
      item.clientName.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q),
  );
  const matchedPayables = accountsPayable.filter(
    (item) =>
      item.supplierName.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q),
  );
  const accountItems = [
    ...matchedReceivables.map((item) => ({
      id: item.id,
      label: `Recebimento — ${item.description}`,
      href: '/financeiro/receber',
    })),
    ...matchedPayables.map((item) => ({
      id: item.id,
      label: `Pagamento — ${item.description}`,
      href: '/financeiro/pagar',
    })),
  ];
  if (accountItems.length) {
    groups.push({ type: 'Contas', items: accountItems.slice(0, 6) });
  }

  const matchedInvoices = invoices.filter((item) => {
    const type = item.type || 'nfse';
    const haystack = [
      item.number,
      item.series,
      item.accessKey,
      item.clientName,
      item.supplierName,
      item.service,
      item.description,
      item.natureOfOperation,
      type,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
  if (matchedInvoices.length) {
    groups.push({
      type: 'Notas',
      items: matchedInvoices.map((item) => {
        const type = (item.type || 'nfse') === 'nfe' ? 'NFe' : 'NFS-e';
        const party = item.clientName || item.supplierName || item.emitter?.name || '—';
        return {
          id: item.id,
          label: `${type} #${item.number} — ${party}`,
          href: '/fiscal/notas',
        };
      }),
    });
  }

  const matchedBillings = billings.filter(
    (item) =>
      item.clientName.toLowerCase().includes(q) ||
      item.contractNumber.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q) ||
      item.period.includes(q),
  );
  if (matchedBillings.length) {
    groups.push({
      type: 'Faturamento',
      items: matchedBillings.map((item) => ({
        id: item.id,
        label: `${item.clientName} — ${item.period}`,
        href: '/fiscal/faturamento',
      })),
    });
  }

  const matchedDocuments = documents.filter(
    (item) =>
      item.name.toLowerCase().includes(q) ||
      item.relatedTo.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q),
  );
  if (matchedDocuments.length) {
    groups.push({
      type: 'Documentos',
      items: matchedDocuments.map((item) => ({
        id: item.id,
        label: item.name,
        href: '/gestao/documentos',
      })),
    });
  }

  const matchedProducts = products.filter((item) => item.name.toLowerCase().includes(q));
  if (matchedProducts.length) {
    groups.push({
      type: 'Produtos',
      items: matchedProducts.map((item) => ({
        id: item.id,
        label: item.name,
        href: '/cadastros/produtos-servicos',
      })),
    });
  }

  const matchedContracts = contracts.filter(
    (item) =>
      item.number.toLowerCase().includes(q) ||
      item.clientName.toLowerCase().includes(q) ||
      item.indexCode.toLowerCase().includes(q),
  );
  if (matchedContracts.length) {
    groups.push({
      type: 'Contratos',
      items: matchedContracts.map((item) => ({
        id: item.id,
        label: `${item.number} — ${item.clientName}`,
        href: '/contratos',
      })),
    });
  }

  const matchedAdjustments = adjustments.filter(
    (item) =>
      item.contractNumber.toLowerCase().includes(q) ||
      item.clientName.toLowerCase().includes(q) ||
      item.indexCode.toLowerCase().includes(q),
  );
  if (matchedAdjustments.length) {
    groups.push({
      type: 'Reajustes',
      items: matchedAdjustments.slice(0, 6).map((item) => ({
        id: item.id,
        label: `${item.contractNumber} — ${item.indexCode}`,
        href: '/contratos/reajustes',
      })),
    });
  }

  const matchedIndices = indices.filter(
    (item) =>
      item.code.toLowerCase().includes(q) ||
      item.name.toLowerCase().includes(q),
  );
  if (matchedIndices.length) {
    groups.push({
      type: 'Índices',
      items: matchedIndices.map((item) => ({
        id: item.id,
        label: `${item.code} — ${item.name}`,
        href: '/contratos/indices',
      })),
    });
  }

  const matchedTx = transactions.filter(
    (item) =>
      item.description.toLowerCase().includes(q) ||
      item.counterparty.toLowerCase().includes(q),
  );
  if (matchedTx.length) {
    groups.push({
      type: 'Movimentações',
      items: matchedTx.slice(0, 4).map((item) => ({
        id: item.id,
        label: `${item.description} — ${item.counterparty}`,
        href: '/financeiro/movimentacoes',
      })),
    });
  }

  const matchedBanks = bankAccounts.filter(
    (item) => item.name.toLowerCase().includes(q) || item.bank.toLowerCase().includes(q),
  );
  if (matchedBanks.length) {
    groups.push({
      type: 'Contas bancárias',
      items: matchedBanks.map((item) => ({
        id: item.id,
        label: item.name,
        href: '/financeiro/contas',
      })),
    });
  }

  const matchedPasswords = passwords.filter(
    (item) => item.service.toLowerCase().includes(q) || item.category.toLowerCase().includes(q),
  );
  if (matchedPasswords.length) {
    groups.push({
      type: 'Senhas',
      items: matchedPasswords.map((item) => ({
        id: item.id,
        label: item.service,
        href: '/gestao/senhas',
      })),
    });
  }

  return groups;
}
